import {
  generatePrefixedActions,
  BLC_STATUS,
  OptionType,
  SetErrorMessage,
} from "../../HISV3/common/CommonInterface";

/*================================ APIS ==============================*/

import ProxyPatientHasDivisionAdminList from "issara-sdk/apis/ProxyPatientHasDivisionAdminList_apps_PRX";
import ProxyPatientHasDivisionDetail from "issara-sdk/apis/ProxyPatientHasDivisionAdminDetail_apps_PRX";
import ProxyPatientListView from "issara-sdk/apis/ProxyPatientListView_apps_PRX";
import ProxyPatientDetail from "issara-sdk/apis/ProxyPatientDetail_apps_PRX";
import ProxyPatientHasDivisionList from "issara-sdk/apis/ProxyPatientHasDivisionList_apps_PRX";
import EmployeeListAPI from "issara-sdk/apis/EmployeeListAPI_users";
import PatientList from "issara-sdk/apis/PatientList_apps_REG";
import PatientDetailView from "issara-sdk/apis/PatientDetailView_apps_REG";
import EncounterList from "issara-sdk/apis/EncounterList_core";
import EncounterPatientListOptimized from "issara-sdk/apis/EncounterPatientListOptimized_core";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import DoctorList from "issara-sdk/apis/DoctorList_core";
import AppointmentList from "issara-sdk/apis/AppointmentList_apps_APP";
import PatientAppointmentView from "issara-sdk/apis/PatientAppointmentView_apps_QUE";
import PatientAppointmentUpdate from "issara-sdk/apis/PatientAppointmentUpdate_apps_QUE";
import AppointmentFinish from "issara-sdk/apis/AppointmentFinish_apps_APP";
import PackageDetail from "issara-sdk/apis/PackageDetail_core";
import Scheduling from "issara-sdk/apis/Scheduling_apps_QUE";
import ConnectUserWithPatient from "issara-sdk/apis/ConnectUserWithPatient_apps_REG";
import EncounterForceCancel from "issara-sdk/apis/EncounterForceCancel_core";

/*================================ SERIALIZERS ==============================*/

import ProxyPatientHasDivisionAdminSerializer from "issara-sdk/types/ProxyPatientHasDivisionAdminSerializer_apps_PRX";
import ProxyPatientListSerializer from "issara-sdk/types/ProxyPatientListSerializer_apps_PRX";
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";
import ProxyPatientHasDivisionSerializer from "issara-sdk/types/ProxyPatientHasDivisionSerializer_apps_PRX";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import DoctorSerializer from "issara-sdk/types/DoctorSerializer_core";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import AppointmentEmployeePositionSerializer from "issara-sdk/types/AppointmentEmployeePositionSerializer_apps_APP";

/*================================ TYPES ==============================*/

import {
  ACTIONS,
  SEQUENCE_NAME,
  MAIN_CARD_NAME,
  StateInitial,
  DataInitial,
  Masters,
  BTN_ACTS,
  State,
  Data,
  Actions,
  SeqState,
  ActionType,
  Params,
  Handler,
  DEFAULT_LIST_LIMIT,
  setButtonLoadCheck,
  setLoadingStatus,
  setErrorMessage,
  setSuccessMessage,
  Controller,
  GetCustomerListParams,
  CreateCustomerParams,
  GetVisitListParams,
  CreateVisitParams,
  GetPatientAppointmentListParams,
  GetAppointmentListParams,
  CreateVisitAppointParams,
  AppointmentFinishResponse,
} from "../types/MeBillingType";

/*================================ UTILS ==============================*/

// import moment from "moment";
import { convertUrlToFile } from "../../HISV3/RHB/RHBInterface";
import getPdfMake from "react-lib/appcon/common/pdfMake";
import { HandleSearchPatientQueue } from "react-lib/apps/MEH/sequence/PatientQueue";
import { HandleGetOrderSummary } from "./PackageOrderSummary";
/*================================ START SEQUENCE ==============================*/

export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  // Init Data
  setLoadingStatus(controller, MAIN_CARD_NAME, true);

  const currentDiv = controller?.data?.division;

  const [
    // common
    employeeListResp,
    doctorListResp,
    // customers
    [customerListResp, customerListErr],
  ] = await Promise.all([
    getEmployeeList(controller, {}),
    getDoctorList(controller, {}),
    getProxyPatientHasDivisionAdminList(controller, {}),
  ]);

  const filteredDoctorList = filterDoctorByEmoployees(doctorListResp, employeeListResp);

  setLoadingStatus(controller, MAIN_CARD_NAME, false);

  controller.setState((prev) => ({
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      sequenceIndex: "Action",
      common: {
        ...prev.MeBillingSequence?.common,
        providerList: employeeListResp,
        doctorList: filteredDoctorList,
      },
      customerList: customerListResp
        ? {
            ...prev.MeBillingSequence?.customerList,
            items: customerListResp?.items || [],
            total: customerListResp?.total || 0,
            activePage: 1,
          }
        : undefined,
    },
  }));

  controller.setState(
    {
      MeBillingSequence: {
        sequenceIndex: "Action",
        orderSummary: controller.getState().MeBillingSequence?.orderSummary,
        common: {
          providerList: employeeListResp,
          doctorList: filteredDoctorList,
        },
        customerList: {
          items: customerListResp?.items || [],
          total: customerListResp?.total || 0,
          activePage: 1,
        },
      },
    },
    () => Action(controller, { action: "GET_CUSTOMER_LIST", ...params }),
  );
};

/*================================ ACTIONS ==============================*/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.GET_ORDER_SUMMARY]: HandleGetOrderSummary,
    [ACTIONS.GET_CUSTOMER_LIST]: HandleGetCustomerList,
    [ACTIONS.CREATE_CUSTOMER]: HandleCreateCustomer,
    [ACTIONS.UPDATE_CUSTOMER]: HandleEditCustomer,
    [ACTIONS.LINK_CUSTOMER]: HandleLinkCustomer,
    [ACTIONS.GET_CUSTOMER_DETAIL]: HandleGetCustomerDetail,
    [ACTIONS.GET_VISIT_LIST]: HandleGetVisitList,
    [ACTIONS.CREATE_VISIT]: HandleCreateVisit,
    [ACTIONS.UPDATE_VISIT]: HandleUpdateVisit,
    [ACTIONS.DELETE_VISIT]: HandleDeleteVisit,
    [ACTIONS.CREATE_VISIT_APPOINTMENT]: HandleCreateVisitAppointment,
    [ACTIONS.GET_APPOINTMENT_LIST]: HandleGetAppointmentList,
    [ACTIONS.GET_PACKAGE_DETAIL]: HandleGetPackageDetail,
    [ACTIONS.GET_PATIENT_DETAIL]: HandleGetPatientDetail,
    [ACTIONS.SELECTED_INITIAL_MENU]: HandleSelectedInitialMenu,
  };

  const action = params.action;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  const callbackHandlers: string[] = [];

  if (handler) {
    if (callbackHandlers.includes(action)) {
      // --- return in callback case
      return handler(controller, params);
    } else {
      // --- void case
      handler(controller, params);
    }
  } else {
    console.warn(`[CustomerInterface] The handler not found for action: ${action}`);
  }
};

/*================================ HANDLERS ==============================*/

const HandleGetCustomerList: Handler<Params<Actions["GET_CUSTOMER_LIST"]>> = async (
  controller,
  params,
) => {
  setButtonLoadCheck(controller, BTN_ACTS.GET_CUSTOMER_LIST, BLC_STATUS.LOADING);

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIST_LIMIT;

  const [customerListResp, customerListErr] = await getProxyPatientHasDivisionAdminList(
    controller,
    { filter: params.filter, offset },
  );

  if (customerListErr || !customerListResp) {
    setButtonLoadCheck(controller, BTN_ACTS.GET_CUSTOMER_LIST, BLC_STATUS.ERROR);
    return;
  }

  setButtonLoadCheck(controller, BTN_ACTS.GET_CUSTOMER_LIST, BLC_STATUS.SUCCESS);

  controller.setState((prev) => ({
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      customerList: {
        ...prev.MeBillingSequence?.customerList,
        activePage,
        items: customerListResp?.items || [],
        total: customerListResp.total || 0,
      },
    },
  }));
};

const HandleGetCustomerDetail: Handler<Params<Actions["GET_CUSTOMER_DETAIL"]>> = async (
  controller,
  params,
) => {
  const { customerId, proxyPatientId } = params;

  setLoadingStatus(controller, MAIN_CARD_NAME, true);

  const [
    [proxyPatientAdminDetailResp, proxyPatientAdminDetailErr],
    // [proxyPatientDetailResp, proxyPatientDetailErr],
    [patientDetailResp, patientDetailErr],
  ] = await Promise.all([
    getProxyPatientHasDivisionAdminDetail(controller, { id: customerId }),
    // getProxyPatientDetail(controller, { id: proxyPatientId }),
    getPatientDetail(controller, { id: proxyPatientId }),
  ]);

  if (
    proxyPatientAdminDetailErr ||
    !proxyPatientAdminDetailResp ||
    // || proxyPatientDetailErr
    // || !proxyPatientDetailResp
    patientDetailErr ||
    !patientDetailResp
  ) {
    const errMessage = proxyPatientAdminDetailErr || patientDetailErr;

    setLoadingStatus(controller, MAIN_CARD_NAME, false);
    setErrorMessage(controller, MAIN_CARD_NAME, errMessage);

    controller.setState((prev) => ({
      MeBillingSequence: {
        ...prev.MeBillingSequence,
        selectedCustomer: undefined,
      },
    }));

    return;
  }

  // get visit list
  const visitListFilter: GetVisitListParams = {
    patient: patientDetailResp.id,
  };

  // get appointment list
  const appListFilter: GetPatientAppointmentListParams = {
    patient_id: patientDetailResp.id,
    exclude_cancel: true,
    division_id: controller.data.division,
    status: "1,2",
    include_appointment_package_confirm: true,
    exclude_appointment_with_encounter: true,
    order_by_app_date: true,
    ordering: ["estimated_at,id"],
  };

  const [[visitListResp, visitListErr], [appListResp, appListErr]] = await Promise.all([
    getVisitiList(controller, { filter: visitListFilter }),
    getPatientAppointmentList(controller, { filter: appListFilter, offset: 0 }),
  ]);

  if (visitListErr || !visitListResp || appListErr || !appListResp) {
    const errMessage = visitListErr || appListErr;

    setErrorMessage(controller, MAIN_CARD_NAME, errMessage);

    controller.setState((prev) => ({
      selectedPatient: patientDetailResp,
      selectedEncounter: {},
      MeBillingSequence: {
        ...prev.MeBillingSequence,
        selectedCustomer: {
          ...prev.MeBillingSequence?.selectedCustomer,
          proxyPatientAdmin: proxyPatientAdminDetailResp,
          patientDetail: patientDetailResp,
        },
      },
    }));

    return;
  }

  setLoadingStatus(controller, MAIN_CARD_NAME, false);

  controller.setState((prev) => ({
    selectedPatient: patientDetailResp,
    selectedEncounter: {},
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      selectedCustomer: {
        ...prev.MeBillingSequence?.selectedCustomer,
        proxyPatientAdmin: proxyPatientAdminDetailResp,
        patientDetail: patientDetailResp,
      },
      visitList: {
        ...prev.MeBillingSequence?.visitList,
        activePage: 1,
        items: visitListResp.items || [],
        total: visitListResp.total || 0,
      },
      appointmentList: {
        ...prev.MeBillingSequence?.appointmentList,
        activePage: 1,
        items: appListResp.items || [],
        total: appListResp.total || 0,
      },
    },
  }));
};

const HandleCreateCustomer: Handler<Params<Actions["CREATE_CUSTOMER"]>> = async (
  controller,
  params,
) => {
  const { customerData } = params;

  setButtonLoadCheck(controller, BTN_ACTS.CREATE_CUSTOMER, BLC_STATUS.LOADING);

  const { citizen_no, passport_no, ...restCustomerData } = customerData;

  const formattedData: CreateCustomerParams = {
    ...restCustomerData,
    ...(customerData.citizen_type === "T" && { citizen_no: customerData.citizen_no || "" }),
    ...(customerData.citizen_type === "F" && { passport_no: customerData.passport_no || "" }),

    // config and options
    create_for_division: controller.data.division,
    birthdate_year_only: false,
    nickname_active: false,
    confirmSave: true,
  };

  const [createdResp, createdErr] = await createPatient(controller, { data: formattedData });

  if (createdErr || !createdResp) {
    setButtonLoadCheck(controller, BTN_ACTS.CREATE_CUSTOMER, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, createdErr);

    return;
  }

  // todo --- เรีียก getProxyPatientDetail ด้วย
  // ??? ---- api ที่เอาไว้ Save Patient Image

  setButtonLoadCheck(controller, BTN_ACTS.CREATE_CUSTOMER, BLC_STATUS.SUCCESS);
  setSuccessMessage(controller, MAIN_CARD_NAME, "บันทึกข้อมูลผู้รับบริการสำเร็จ");

  controller.setState((prev) => ({
    selectedPatient: createdResp,
    selectedEncounter: {},
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      selectedCustomer: {
        ...prev.MeBillingSequence?.selectedCustomer,
        patientDetail: createdResp,
      },
    },
  }));

  if (params.onSuccess) {
    params.onSuccess();
  }
};

const HandleEditCustomer: Handler<Params<Actions["UPDATE_CUSTOMER"]>> = async (
  controller,
  params,
) => {
  const { customerData, customerId } = params;

  setButtonLoadCheck(controller, BTN_ACTS.UPDATE_CUSTOMER, BLC_STATUS.LOADING);

  const { display_hn, citizen_no, passport_no, profile_image, ...restCutomerData } = customerData;
  const { created, edited, ...restPresentAddress } = customerData.present_address as any;
  const { id, ...restProfileImage } = (profile_image || {}) as any;

  const isImageChanged =
    typeof profile_image?.image === "string" && profile_image.image.startsWith("data:");

  const formattedData: CreateCustomerParams = {
    ...restCutomerData,

    present_address: restPresentAddress,

    ...(customerData.citizen_type === "T" && { citizen_no: customerData.citizen_no || "" }),
    ...(customerData.citizen_type === "F" && { passport_no: customerData.passport_no || "" }),

    ...(isImageChanged ? { profile_image } : ({ profile_image: { id } } as any)),

    // config
    birthdate_year_only: false,
    nickname_active: false,
    confirmSave: true,
    create_for_division: controller.data.division,
  };

  const [updatedResp, updatedErr] = await updatePatient(controller, {
    id: customerId,
    data: formattedData,
  });

  if (updatedErr || !updatedResp) {
    setButtonLoadCheck(controller, BTN_ACTS.UPDATE_CUSTOMER, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, updatedErr);

    return;
  }

  setButtonLoadCheck(controller, BTN_ACTS.UPDATE_CUSTOMER, BLC_STATUS.SUCCESS);
  setSuccessMessage(controller, MAIN_CARD_NAME, "บันทึกข้อมูลผู้รับบริการสำเร็จ");

  // todo --- เรียก getProxyPatientDetail ด้วย

  controller.setState((prev) => ({
    selectedPatient: updatedResp,
    selectedEncounter: {},
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      selectedCustomer: {
        ...prev.MeBillingSequence?.selectedCustomer,
        patientDetail: updatedResp,
      },
    },
  }));

  if (params.onSuccess) {
    params.onSuccess();
  }
};

const HandleLinkCustomer: Handler<Params<Actions["LINK_CUSTOMER"]>> = async (
  controller,
  params,
) => {
  const { customerId, linkedId } = params;

  setButtonLoadCheck(controller, BTN_ACTS.LINK_CUSTOMER, BLC_STATUS.LOADING);

  const [, err] = await ConnectUserWithPatient.post({
    apiToken: controller.apiToken,
    data: {
      patient: customerId,
      user_patient: Number(linkedId),
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) {
    setButtonLoadCheck(controller, BTN_ACTS.LINK_CUSTOMER, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, err);

    return;
  }

  setButtonLoadCheck(controller, BTN_ACTS.LINK_CUSTOMER, null);
  setSuccessMessage(controller, MAIN_CARD_NAME, "เชื่อมไอดีผู้ใช้งานสำเร็จ");

  if (params.onSuccess) {
    params.onSuccess();
  }
};

const HandleGetVisitList: Handler<Params<Actions["GET_VISIT_LIST"]>> = async (
  controller,
  params,
) => {
  const { filter } = params;

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIST_LIMIT;

  const [visitListResp, visitListErr] = await getVisitiList(controller, { filter, offset });

  if (visitListErr || !visitListResp) {
    setErrorMessage(controller, MAIN_CARD_NAME, visitListErr);

    return;
  }

  controller.setState((prev) => ({
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      visitList: {
        ...prev.MeBillingSequence?.visitList,
        activePage,
        items: visitListResp?.items || [],
        total: visitListResp?.total || 0,
      },
    },
  }));
};

const HandleCreateVisit: Handler<Params<Actions["CREATE_VISIT"]>> = async (controller, params) => {
  const state = controller.getState();
  const { visitData } = params;

  setButtonLoadCheck(controller, BTN_ACTS.CREATE_VISIT, BLC_STATUS.LOADING);

  const formattedData: CreateVisitParams = {
    ...visitData,

    // hard coded (สำหรับ create step)
    division: controller.data.division,
  };

  const [createdResp, createdErr] = await createVisit(controller, { data: formattedData });

  if (createdErr || !createdResp) {
    setButtonLoadCheck(controller, BTN_ACTS.CREATE_VISIT, BLC_STATUS.ERROR);
    setErrorMessage(controller, MAIN_CARD_NAME, createdErr);

    return;
  }

  setButtonLoadCheck(controller, BTN_ACTS.CREATE_VISIT, BLC_STATUS.SUCCESS);

  // update visit list
  const visitListFilter: GetVisitListParams = {
    doctor: visitData.doctor,
    patient: visitData.patient,
  };

  const [visitListResp, visitListErr] = await getVisitiList(controller, {
    filter: visitListFilter,
  });

  if (visitListErr || !visitListResp) {
    setErrorMessage(controller, MAIN_CARD_NAME, visitListErr);

    return;
  }

  controller.setState((prev) => ({
    selectedEncounter: createdResp,
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      selectedVisit: createdResp,
      visitList: {
        ...prev.MeBillingSequence?.visitList,
        activePage: 1,
        items: visitListResp?.items || [],
        total: visitListResp?.total || 0,
      },
    },
  }));

  if (params.onSuccess) {
    params.onSuccess();
  }

  // reset state
  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPDATE_VISIT]: null },
  });
};

const HandleCreateVisitAppointment: Handler<Params<Actions["CREATE_VISIT_APPOINTMENT"]>> = async (
  controller,
  params,
) => {
  const { appointmentId, patient } = params;

  const btnKey = `${BTN_ACTS.CREATE_VISIT_APPOINTMENT}_${appointmentId}`;

  setButtonLoadCheck(controller, btnKey, BLC_STATUS.LOADING);

  const [patientAppUpdatedResp, patientAppUpdatedErr] = await getPatientAppointment(controller, {
    appointment: appointmentId,
  });

  let doctorId = params.doctorId ?? patientAppUpdatedResp?.doctor_id;

  if (params.providerName && !params.doctorId) {
    const [provider] = await getDoctorProviderSearchList(controller, {
      searchText: params.providerName,
    });

    if (provider?.doctor?.id) {
      doctorId = provider.doctor.id;
    }
  }

  if (patientAppUpdatedErr || !patientAppUpdatedResp) {
    setErrorMessage(controller, MAIN_CARD_NAME, patientAppUpdatedErr);
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);

    return;
  }

  // get appointment list
  // const appFilter: GetAppointmentListParams = {
  //   patient,
  //   doctor: patientAppUpdatedResp?.doctor_id,
  //   division: controller.data.division,
  //   exclude_canceled: true,
  //   exclude_finished: true,
  // };

  // const [appListResp, appListErr] = await getAppointmentList(controller, { filter: appFilter });

  // if (appListErr || !appListResp) {
  //   setErrorMessage(controller, MAIN_CARD_NAME, appListErr);
  //   setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);

  //   return;
  // }

  // const filteredAppList: AppointmentEmployeePositionSerializer[] = (appListResp.items || [])
  //   .filter(
  //     (item: AppointmentEmployeePositionSerializer) => item.id === patientAppUpdatedResp.content_id
  //   )
  //   .map((item: AppointmentEmployeePositionSerializer) => ({
  //     ...item,
  //     checked: true,
  //   }));

  // open encounter
  const openVisitData = {
    items: [
      {
        id: patientAppUpdatedResp.content_id,
        checked: true,
        doctor: doctorId,
        doctor_id: doctorId,
      },
    ],
  };

  const [openEnResp, openEnErr] = await createVisitAppointment(controller, { data: openVisitData });

  if (openEnErr || !openEnResp) {
    setErrorMessage(controller, MAIN_CARD_NAME, openEnErr);
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);

    return;
  }

  const selectedVisit = openEnResp.encounters[0];

  const visitFilter: GetVisitListParams = {
    patient,
    doctor: patientAppUpdatedResp?.doctor_id,
  };

  const patientAppListFilter: GetPatientAppointmentListParams = {
    patient_id: patient,
    exclude_cancel: true,
    division_id: controller.data.division,
    status: "1,2",
    include_appointment_package_confirm: true,
    exclude_appointment_with_encounter: true,
    order_by_app_date: true,
    ordering: ["estimated_at,id"],
  };

  // update visit list
  const [
    [visitListResp, visitListErr],
    [visitDetailResp, visitDetailErr],
    [patientAppListResp, patientAppListErr],
  ] = await Promise.all([
    getVisitiList(controller, { filter: visitFilter }),
    getVisitDetail(controller, { id: selectedVisit }),
    getPatientAppointmentList(controller, { filter: patientAppListFilter, offset: 0 }),
  ]);

  if (
    visitListErr ||
    !visitListResp ||
    visitDetailErr ||
    !visitDetailResp ||
    patientAppListErr ||
    !patientAppListResp
  ) {
    const errMessage = visitListErr || visitDetailErr || patientAppListErr;

    setErrorMessage(controller, MAIN_CARD_NAME, errMessage);
    setButtonLoadCheck(controller, btnKey, BLC_STATUS.ERROR);

    return;
  }

  setButtonLoadCheck(controller, btnKey, BLC_STATUS.SUCCESS);

  controller.setState((prev) => ({
    selectedEncounter: visitDetailResp,
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      visitList: {
        ...prev.MeBillingSequence?.visitList,
        activePage: 1,
        items: visitListResp.items || [],
        total: visitListResp.total || 0,
      },
      selectedVisit: visitDetailResp,
      appointmentList: {
        ...prev.MeBillingSequence?.appointmentList,
        activePage: 1,
        items: patientAppListResp.items || [],
        total: patientAppListResp.total || 0,
      },
    },
  }));

  if (params.onSuccess) {
    params.onSuccess();
  }
};

const HandleUpdateVisit: Handler<Params<Actions["UPDATE_VISIT"]>> = async (controller, params) => {
  const { visitData } = params;

  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPDATE_VISIT]: BLC_STATUS.LOADING },
  });

  const [encounterDetailRes, encounterDetailErr] = await EncounterDetail.update({
    apiToken: controller.apiToken,
    data: {
      chief_complaints: state.selectedEncounter?.chief_complaints,
      location: state.selectedEncounter?.location,
      number: state.selectedEncounter?.number,
      pk: state.selectedEncounter?.pk,
      type: "OPD",
      patient: state.selectedEncounter?.patient,
      division: state.selectedEncounter?.division,
      doctor: visitData?.doctor,
      action: "OPD_EDIT",
    },
    pk: state.selectedEncounter?.id,
  });

  if (encounterDetailRes) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPDATE_VISIT]: BLC_STATUS.SUCCESS },
    });

    if (params.onSuccess) {
      params.onSuccess();
    }
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPDATE_VISIT]: BLC_STATUS.ERROR },
    });
  }

  // reset state
  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPDATE_VISIT]: null },
  });
};

const HandleDeleteVisit: Handler<Params<Actions["DELETE_VISIT"]>> = async (controller, params) => {
  const state = controller.getState();

  const [encounterDetailRes, encounterDetailErr] = await EncounterForceCancel.delete({
    pk: state.selectedEncounter?.pk,
    apiToken: controller.apiToken,
    extra: {
      data: {
        username: params.username,
        password: params.password,
        note: params.note,
      },
      division: controller.data.division,
      device: controller.data.device,
    },
  });

  if (encounterDetailErr) {
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [params?.sequence]: encounterDetailErr,
      },
    });
  } else {
    HandleSearchPatientQueue(controller, {
      sequence: "PatientQueue",
      action: "SEARCH",
    });

    if (params.onSuccess) {
      params.onSuccess();
    }
  }
};

const HandleGetAppointmentList: Handler<Params<Actions["GET_APPOINTMENT_LIST"]>> = async (
  controller,
  params,
) => {
  const { filter } = params;

  setButtonLoadCheck(controller, BTN_ACTS.GET_APPOINTMENT_LIST, BLC_STATUS.LOADING);

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIST_LIMIT;

  const formattedFilter: GetPatientAppointmentListParams = {
    patient_id: filter?.patient_id,
    exclude_cancel: true,
    division_id: controller.data.division,
    status: "1,2",
    include_appointment_package_confirm: true,
    exclude_appointment_with_encounter: true,
    order_by_app_date: true,
    ordering: ["estimated_at,id"],
    ...(filter?.id__in && { id__in: filter.id__in }),
  };

  const [appListResp, appListErr] = await getPatientAppointmentList(controller, {
    filter: formattedFilter,
    offset,
  });

  if (appListErr || !appListResp) {
    setErrorMessage(controller, MAIN_CARD_NAME, appListErr);
    setButtonLoadCheck(controller, BTN_ACTS.GET_APPOINTMENT_LIST, BLC_STATUS.ERROR);

    return;
  }

  setButtonLoadCheck(controller, BTN_ACTS.GET_APPOINTMENT_LIST, BLC_STATUS.SUCCESS);

  controller.setState((prev) => ({
    MeBillingSequence: {
      ...prev.MeBillingSequence,
      appointmentList: {
        ...prev.MeBillingSequence?.appointmentList,
        activePage,
        items: appListResp.items || [],
        total: appListResp.total || 0,
      },
    },
  }));
};

const HandleGetPackageDetail: Handler<Params<"GET_PACKAGE_DETAIL">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.GET_PACKAGE_DETAIL}_${params.index}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const promiseArr = params.packageIds.map((id) =>
    PackageDetail.retrieve({
      apiToken: controller.apiToken,
      pk: id,
    }),
  );

  const responses = await Promise.all(promiseArr);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
    MeBillingSequence: {
      ...state.MeBillingSequence,
      packageDetail: responses.map(([res]: any) => res as Record<string, any>),
    },
  });
};

const HandleGetPatientDetail: Handler<Params<"GET_PATIENT_DETAIL">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  const { patientID } = params;

  const [[patientDetailResp, patientDetailErr]] = await Promise.all([
    getPatientDetail(controller, { id: patientID }),
  ]);

  if (patientDetailErr || !patientDetailResp) {
    const errMessage = patientDetailErr;

    setLoadingStatus(controller, MAIN_CARD_NAME, false);
    setErrorMessage(controller, MAIN_CARD_NAME, errMessage);

    controller.setState((prev) => ({
      MeBillingSequence: {
        ...prev.MeBillingSequence,
        selectedPatient: undefined,
      },
    }));

    return;
  }

  if (patientDetailResp) {
    const emailValue = patientDetailResp?.email || patientDetailResp?.present_address?.email || "";

    const { created, edited, ...relative_address } = patientDetailResp?.relative_address ?? {};

    const customerFormData: CreateCustomerParams = {
      id: patientDetailResp?.id,
      hn: patientDetailResp?.hn,
      // -- iden
      citizen_type: patientDetailResp?.citizen_type || ("" as any),
      citizen_no: patientDetailResp?.citizen_no || "", // --- citizen_type === "T"
      passport_no: patientDetailResp?.passport_no || "", // --- citizen_type === "F"
      // -- thai name
      pre_name_th: patientDetailResp?.pre_name_th || 0,
      first_name_th: patientDetailResp?.first_name_th || "",
      last_name_th: patientDetailResp?.last_name_th || "",
      // -- eng name
      pre_name_en: patientDetailResp?.pre_name_en || 0,
      first_name_en: patientDetailResp?.first_name_en || "",
      middle_name_en: patientDetailResp?.middle_name_en || "",
      last_name_en: patientDetailResp?.last_name_en || "",
      //
      birthdate: patientDetailResp?.birthdate || "",
      gender: patientDetailResp?.gender || "",
      // -- address
      present_address: patientDetailResp?.present_address,
      // -- extended
      email: emailValue || "",
      line_id: patientDetailResp?.line_id || "",
      profile_image: patientDetailResp?.profile_image || null,
      // -- relative_address
      relative_address: relative_address,

      // display only
      display_hn: patientDetailResp?.division_hn || "",
    };

    controller.setState((prev) => ({
      selectedPatient: customerFormData,
    }));

    if (params.onSuccess) {
      params.onSuccess();
    }
  }
};

const HandleSelectedInitialMenu: Handler<Params<"SELECTED_INITIAL_MENU">> = async (
  controller,
  params,
) => {
  const [resp] = await ProxyPatientHasDivisionAdminList.list({
    apiToken: controller.apiToken,
    params: { patient: params.data?.patient, limit: 1 },
    extra: { division: controller.data.division },
  });

  const customer = resp?.items?.[0];

  if (!customer?.id || !customer?.proxy_patient) return;

  if (params.initialMenu) {
    controller.setState({
      meBillingActiveTab: 0,
      meBillingInitialMenu: params.initialMenu,
      receiptId: Number(params.data?.receipt) || null,
    });
  }

  await HandleGetCustomerDetail(controller, {
    sequence: "MeBilling",
    action: "GET_CUSTOMER_DETAIL",
    customerId: customer.id,
    proxyPatientId: customer.proxy_patient,
  });
};

/*================================ API HANDLES ==============================*/

const getProxyPatientHasDivisionAdminList: Handler<{
  filter?: GetCustomerListParams;
  offset?: number;
}> = async (controller, params) => {
  const { filter, offset } = params;

  const [resp, err] = await ProxyPatientHasDivisionAdminList.list({
    apiToken: controller.apiToken,
    params: {
      ...filter,
      offset,
      limit: DEFAULT_LIST_LIMIT,
    },
    extra: { division: controller.data.division },
  });

  if (err) return [null, err];

  return [resp, null];
};

const getProxyPatientHasDivisionAdminDetail: Handler<
  { id: number },
  Promise<[ProxyPatientHasDivisionAdminSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await ProxyPatientHasDivisionDetail.get({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as ProxyPatientHasDivisionAdminSerializer, null];
};

const getPatientDetail: Handler<{ id: number }, Promise<[PatientSerializer | null, any]>> = async (
  controller,
  params,
) => {
  const [resp, err] = await PatientDetailView.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as PatientSerializer, null];
};

const getProxyPatientDetail: Handler<
  { id: number },
  Promise<[ProxyPatientSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await ProxyPatientDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (err) return [null, err];

  return [resp as ProxyPatientSerializer, null];
};

const getEmployeeList: Handler<{}, Promise<EmployeeSerializer[]>> = async (controller, params) => {
  const [resp, err] = await EmployeeListAPI.list({
    apiToken: controller.apiToken,
    params: {
      ordering: "id",
      only_employee_in_division: true,
      limit: 9999,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [];

  return resp.items || ([] as EmployeeSerializer[]);
};

const getDoctorList: Handler<{}, Promise<DoctorSerializer[]>> = async (controller, params) => {
  const [resp, err] = await DoctorList.list({
    apiToken: controller.apiToken,
    params: {
      limit: 9999,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [];

  return resp.items || ([] as DoctorSerializer[]);
};

const createPatient: Handler<
  { data: CreateCustomerParams },
  Promise<[PatientSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await PatientList.create({
    apiToken: controller.apiToken,
    data: params.data,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as PatientSerializer, null];
};

const updatePatient: Handler<
  { id: number; data: CreateCustomerParams },
  Promise<[PatientSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await PatientDetailView.patch({
    apiToken: controller.apiToken,
    pk: params.id,
    data: params.data,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as PatientSerializer, null];
};

const getVisitiList: Handler<{ filter?: GetVisitListParams; offset?: number }> = async (
  controller,
  params,
) => {
  const [resp, err] = await EncounterPatientListOptimized.get({
    apiToken: controller.apiToken,
    params: {
      ...params.filter,
      division: controller.data.division, // ****
      // offset: params.offset,
      limit: 9999, // ****
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp, null];
};

const getVisitDetail: Handler<{ id: number }, Promise<[EncounterSerializer | null, any]>> = async (
  controller,
  params,
) => {
  const [resp, err] = await EncounterDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as EncounterSerializer, null];
};

const createVisit: Handler<
  { data: CreateVisitParams },
  Promise<[EncounterSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await EncounterList.create({
    apiToken: controller.apiToken,
    data: params.data,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as EncounterSerializer, null];
};

const updateVisit: Handler<
  { id: number; data: CreateVisitParams },
  Promise<[EncounterSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await EncounterDetail.patch({
    apiToken: controller.apiToken,
    pk: params.id,
    data: params.data,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as EncounterSerializer, null];
};

const getPatientAppointmentList: Handler<{
  filter?: GetPatientAppointmentListParams;
  offset?: number;
}> = async (controller, params) => {
  const { filter, offset } = params;

  const [resp, err] = await PatientAppointmentView.list({
    apiToken: controller.apiToken,
    params: {
      ...filter,
      offset,
      limit: DEFAULT_LIST_LIMIT,
    },
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp, null];
};

const getPatientAppointment: Handler<
  { appointment: number }, // ***
  Promise<[PatientAppointmentSerializer | null, any]>
> = async (controller, params) => {
  const [resp, err] = await PatientAppointmentUpdate.retrieve({
    apiToken: controller.apiToken,
    pk: params.appointment,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as PatientAppointmentSerializer, null];
};

const getAppointmentList: Handler<{ filter?: GetAppointmentListParams }> = async (
  controller,
  params,
) => {
  const { filter } = params;

  const [resp, err] = await AppointmentList.list({
    apiToken: controller.apiToken,
    params: filter,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp, null];
};

const createVisitAppointment: Handler<
  { data: CreateVisitAppointParams },
  Promise<[AppointmentFinishResponse | null, any]>
> = async (controller, params) => {
  const [resp, err] = await AppointmentFinish.post({
    apiToken: controller.apiToken,
    data: params.data,
    extra: { division: controller.data.division },
  });

  if (err || !resp) return [null, err];

  return [resp as AppointmentFinishResponse, null];
};

export const getDoctorProviderSearchList: Handler = async (controller, params) => {
  const [provider, doctor] = await Promise.all([
    Scheduling.get({
      command: "ProviderSearch",
      params: { searchText: params.searchText },
      apiToken: controller.apiToken,
    }),
    DoctorList.list({
      apiToken: controller.apiToken,
      params: {
        search: params.searchText,
      },
    }),
  ]);

  return (provider[0] ?? []).map((item: any) => {
    const foundDoctor = (doctor[0]?.items ?? []).find((acc: any) =>
      acc.provider.includes(item.provider_id),
    );

    return {
      specialty_name_list: item.specialty_name_list,
      provider_id: item.provider_id,
      image: foundDoctor?.image,
      gender: foundDoctor?.gender,
      full_name: foundDoctor?.full_name,
      doctor_id: foundDoctor?.id,
      doctor: foundDoctor,
    };
  });
};

/*================================ HELPERS ==============================*/

const filterDoctorByEmoployees = (
  doctors: DoctorSerializer[],
  employees: EmployeeSerializer[],
): DoctorSerializer[] => {
  const empIds = new Set(employees.map((emp) => emp.id));

  return doctors.filter((doctor) => doctor.employee && empIds.has(doctor.employee));
};
